import React, {useMemo} from 'react';
import {
	ReactHookFormSelectField,
	type ReactHookFormSelectFieldProps,
} from '@netx/core-components/Form/SelectField';
import type {ICustomFormItem, CustomFormFormValues} from '../../types';
import {
	getCustomFormItemTranslatedLabel,
	getCustomFormBaseFieldName,
} from '../../utils';
import {getSelectOptionDisabled, getSelectOptionLabel} from './utils';

export interface ReactHookFormCustomFormItemMultiselectProps<
	TValues extends CustomFormFormValues = CustomFormFormValues,
> extends Omit<
		ReactHookFormSelectFieldProps<string, true, false, false, TValues>,
		'label' | 'name' | 'required'
	> {
	item: ICustomFormItem;
}

export const ReactHookFormCustomFormItemMultiselectPrimitive = <
	TValues extends CustomFormFormValues = CustomFormFormValues,
>({
	item,
	...props
}: ReactHookFormCustomFormItemMultiselectProps<TValues>) => {
	const {label, value, required, userValues} = item;

	if (typeof value === 'string') return null;

	const options = useMemo(() => {
		return value.reduce<string[]>((accum, val) => {
			if (typeof val === 'string') return [...accum, val];
			if (val.disabled && !(userValues as string[]).includes(val.key))
				return accum;
			return [...accum, val.key];
		}, []);
	}, [value, userValues]);

	return (
		<ReactHookFormSelectField<string, true, false, false, TValues>
			fullWidth
			{...props}
			multiple
			disabled={props.disabled || item.disabled}
			label={getCustomFormItemTranslatedLabel(label)}
			name={getCustomFormBaseFieldName(item)}
			required={required}
			options={options}
			getOptionDisabled={getSelectOptionDisabled(value)}
			getOptionLabel={getSelectOptionLabel(value)}
		/>
	);
};

export const ReactHookFormCustomFormItemMultiselect =
	ReactHookFormCustomFormItemMultiselectPrimitive;

export default ReactHookFormCustomFormItemMultiselect;
